import { useEffect, useState } from 'react';

import { useDispatch } from 'react-redux';

import { useMarkFreshStrawNoStimNoFlushMutation } from '@/app/services/freshStrawRest';
import { addToast } from '@/app/slices/toast';
import { ToastLife } from '@/components/hoc/toast-container/enums';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface FreshStrawNoStimNoFlushDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  flushId: string | null;
  flushLabel?: string;
  onMarked?: () => void;
}

export function FreshStrawNoStimNoFlushDialog({
  open,
  onOpenChange,
  flushId,
  flushLabel,
  onMarked,
}: FreshStrawNoStimNoFlushDialogProps) {
  const dispatch = useDispatch();
  const [comment, setComment] = useState('');
  const [markNoStim, { isLoading }] = useMarkFreshStrawNoStimNoFlushMutation();

  useEffect(() => {
    if (open) setComment('');
  }, [open, flushId]);

  const handleConfirm = async () => {
    if (!flushId) return;
    try {
      await markNoStim({ flushId, comment: comment || undefined }).unwrap();
      dispatch(
        addToast({
          severity: 'success',
          summary: 'Flush marked No Stim / No Flush',
          detail: 'The flush status was updated.',
          life: ToastLife.short,
        }),
      );
      onMarked?.();
      onOpenChange(false);
    } catch (err) {
      const message =
        err && typeof err === 'object' && 'data' in err
          ? ((err as { data?: { message?: string } }).data?.message ??
            'Mark failed.')
          : 'Mark failed.';
      dispatch(
        addToast({
          severity: 'error',
          summary: 'Could not mark flush',
          detail: message,
          life: ToastLife.long,
        }),
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            No Stim / No Flush
            {flushLabel && (
              <span className="ml-2 text-sm font-normal text-gray-500">
                · {flushLabel}
              </span>
            )}
          </DialogTitle>
        </DialogHeader>
        <DialogBody>
          <div className="flex flex-col gap-3 text-sm">
            <p className="text-gray-700">
              Mark this flush as No Stim / No Flush. The flush status will be
              set and the comment (if any) will be written to the flush record.
            </p>
            <label className="flex flex-col gap-1">
              <span className="text-xs font-medium text-gray-600">
                Comment (optional)
              </span>
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                rows={3}
                className="w-full rounded border border-gray-300 px-2 py-1 text-sm"
              />
            </label>
          </div>
        </DialogBody>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={!flushId || isLoading}>
            {isLoading ? 'Marking…' : 'Mark No Stim / No Flush'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
